import { api } from "@/api";
import type { FetchRequestInit } from "@/api";
import { IEmirexPaymentRequest, IFinanceFilter } from "@/interfaces/mlm";
import router from "@/router";
import { AxiosError } from "axios";
import { getStoreAccessors } from "typesafe-vuex";
import { ActionContext } from "vuex";
import { dispatchCheckApiError, dispatchCheckFormError } from "../main/actions";
import { State } from "../state";
import {
  commitSetActivePackage,
  commitSetBinaryTree,
  commitSetCompanyStats,
  commitSetInvoiceResponse,
  commitSetPackagesList,
  commitSetQualification,
  commitSetRecentRegistrations,
  commitSetTransactionsResponse,
  commitSetUserRefInfo,
  commitSetSigmaRings,
  commitSetUserSigmaRings,
  commitSetXcoinPackagesList,
  commitSetXcoinActivePackages,
  commitSetUserXcoinPackagesList,
  commitSetPhysicalNodesList,
  commitSetVirtualNodesList,
  commitSetVirtualNodesActive,
  commitSetPhysicalNodesActive,
  commitSetMonthlyTurnover
} from "./mutations";
import { MlmState } from "./state";

type MainContext = ActionContext<MlmState, State>;

export const actions = {
  async actionAuthFetch(
    context: MainContext,
    payload: { input: string; init?: FetchRequestInit }
  ): Promise<Response> {
    return api.authFetch(
      context.rootState.main.token,
      payload.input,
      payload.init
    );
  },
  async actionGetPackages(context: MainContext) {
    try {
      const response = await api.getPackagesList(context.rootState.main.token);
      if (response.data) {
        commitSetPackagesList(context, response.data);
      }
    } catch (error) {
      commitSetPackagesList(context, []);
      await dispatchCheckApiError(context, error);
    }
  },
  async actionGetPackagesTerms(context: MainContext) {
    try {
      const response = await api.getPackagesTerms(context.rootState.main.token);
      return response.data;
    } catch (error) {
      // await dispatchCheckApiError(context, error);
      return [];
    }
  },
  async actionGetActivePackage(context: MainContext) {
    try {
      const response = await api.getActivePackage(context.rootState.main.token);
      if (response.data) {
        commitSetActivePackage(context, response.data);
      }
    } catch (error) {
      commitSetActivePackage(context, null);
      await dispatchCheckApiError(context, error);
    }
  },
  async actionMakeProfixoneLink(context: MainContext) {
    try {
      const response = await api.makeProfixoneLink(
        context.rootState.main.token
      );
      return response.data;
    } catch (error) {
      await dispatchCheckApiError(context, error);
      return null;
    }
  },
  async actionGetCompanyStats(context: MainContext) {
    try {
      const response = await api.getCompanyStats(context.rootState.main.token);
      commitSetCompanyStats(context, response.data);
    } catch (error) {
      await dispatchCheckApiError(context, error);
      commitSetCompanyStats(context, {
        total_devices: 0,
        packages_500_count: 0,
        purchased_devices_count: 0,
      });
    }
  },
  async actionGetQualification(context: MainContext) {
    try {
      const response = await api.getQualification(context.rootState.main.token);
      if (response.data) {
        commitSetQualification(context, response.data);
      }
    } catch (error) {
      commitSetQualification(context, null);
      await dispatchCheckApiError(context, error);
    }
  },
  async actionGetOrCreateInvoice(
    context: MainContext,
    data: { package: number; standart: string; token?: string }
  ) {
    try {
      const response = await api.getOrCreateInvoice(
        context.rootState.main.token,
        data
      );
      if (response.data) {
        commitSetInvoiceResponse(context, response.data);
      }
    } catch (error) {
      commitSetInvoiceResponse(context, null);
      await dispatchCheckApiError(context, error);
    }
  },
  async actionProcessConfirmInvoice(
    context: MainContext,
    data: { invoice: number, email: string }
  ) {
    try {
      const response = await api.processConfirmInvoice(
        context.rootState.main.token,
        data
      );
      if (response.data) {
        commitSetInvoiceResponse(context, response.data);
      }
      return true;
    } catch (error) {
      // commitSetInvoiceResponse(context, null);
      // await dispatchCheckApiError(context, error);
      return error.response.data;
    }
  },
  // async actionGetOrCreateInvoice(context: MainContext, data: {package: number, standart: string, token?: string}) {
  //     try {
  //         const response = await api.getOrCreateInvoice(context.rootState.main.token, data);
  //         if (response.data) {
  //             commitSetInvoiceResponse(context, response.data);
  //         }
  //     } catch (error) {
  //         commitSetInvoiceResponse(context, null);
  //         await dispatchCheckApiError(context, error);
  //     }
  // },
  // async actionGetOrCreateUpgradeInvoice(context: MainContext, data: {package: number, standart: string, token?: string}) {
  //     try {
  //         const response = await api.getOrCreateUpgradeInvoice(context.rootState.main.token, data);
  //         if (response.data) {
  //             commitSetInvoiceResponse(context, response.data);
  //         }
  //     } catch (error) {
  //         commitSetInvoiceResponse(context, null);
  //         await dispatchCheckApiError(context, error);
  //     }
  // },
  async actionGetOrCreateOrderInvoice(
    context: MainContext,
    data: { order?: number | null; standart: string; token?: string }
  ) {
    try {
      const response = await api.getOrCreateOrderInvoice(
        context.rootState.main.token,
        data
      );
      if (response.data) {
        commitSetInvoiceResponse(context, response.data);
      }
    } catch (error) {
      commitSetInvoiceResponse(context, null);
      await dispatchCheckApiError(context, error);
    }
  },
  async actionGetPaymentStatus(
    context: MainContext,
    data: { address: string; invoice: number }
  ) {
    try {
      const response = await api.getPaymentStatus(
        context.rootState.main.token,
        data
      );
      return response.data;
    } catch (error) {
      await dispatchCheckApiError(context, error);
      return null;
    }
  },
  async actionProcessPurchaseByUnit(
    context: MainContext,
    data: { invoice: number }
  ) {
    try {
      const response = await api.processPurchaseByUnit(
        context.rootState.main.token,
        data
      );
      return true;
    } catch (error) {
      await dispatchCheckApiError(context, error);
      return null;
    }
  },
  async actionGetTransactionsList(
    context: MainContext,
    payload: { filter: IFinanceFilter; page: string; }
  ) {
    try {
      const response = await api.getTransactionsList(
        context.rootState.main.token,
        payload.filter,
        payload.page,
      );
      commitSetTransactionsResponse(context, response.data);
    } catch (error) {
      commitSetTransactionsResponse(context, {
        next: "",
        previous: "",
        current: 0,
        count: 0,
        results: [],
      });
      await dispatchCheckApiError(context, error);
    }
  },
  async actionRequestEmirexPayment(
    context: MainContext,
    payload: IEmirexPaymentRequest
  ) {
    try {
      const response = await api.requestEmirexPayment(
        context.rootState.main.token,
        payload
      );
      return response.data;
    } catch (error) {
      await dispatchCheckApiError(context, error);
      return null;
    }
  },
  async actionGetEmirexEuroRate(context: MainContext) {
    try {
      const response = await api.getEmirexEuroRate(
        context.rootState.main.token
      );
      return response.data;
    } catch (error) {
      await dispatchCheckApiError(context, error);
      return null;
    }
  },
  async actionValidateDexnetAddress(context: MainContext, address: string) {
    try {
      const response = await api.validateDexnetAddress(
        context.rootState.main.token,
        address
      );
      return response;
    } catch (error) {
      await dispatchCheckApiError(context, error);
      return null;
    }
  },
  async actionCheckDexfreedomTokenInfo(context: MainContext) {
    try {
      const response = await api.checkDexfreedomTokenInfo(
        context.rootState.main.token
      );
      return response;
    } catch (error) {
      await dispatchCheckApiError(context, error);
      return null;
    }
  },
  async actionSubmitWithdrawRequest(
    context: MainContext,
    data: {
      amount: number;
      standart: string;
      token: string;
      wallet_address: string;
      security_code: string;
    }
  ) {
    try {
      const response = await api.submitWithdrawRequest(
        context.rootState.main.token,
        data
      );
      return response.data;
    } catch (error) {
      await dispatchCheckApiError(context, error);
      return null;
    }
  },
  async actionGetUserBinaryObject(
    context: MainContext,
    data: { user: number }
  ) {
    try {
      const response = await api.getUserBinaryObject(
        context.rootState.main.token,
        data
      );
      // if (response.data) {
      //     commitSetActivePackage(context, response.data);
      // }
    } catch (error) {
      // commitSetActivePackage(context, null);
      await dispatchCheckApiError(context, error);
    }
  },
  async actionGetPurchaseStatisticsByPackages(context: MainContext, data: any) {
    try {
      const response = await api.getPurchaseStatisticsByPackages(
        context.rootState.main.token,
        data
      );
      return response.data;
    } catch (error) {
      // commitSetActivePackage(context, null);
      await dispatchCheckApiError(context, error);
      return {
        binary_bonus: 0,
        purchase_statistics: [],
      };
    }
  },
  async actionGetUserBinaryTree(context: MainContext, data: { user: number }) {
    try {
      const response = await api.getUserBinaryTree(
        context.rootState.main.token,
        data
      );
      if (response.data) {
        commitSetBinaryTree(context, response.data);
      }
      return response.data;
    } catch (error) {
      commitSetBinaryTree(context, {});
      await dispatchCheckApiError(context, error);
      return null;
    }
  },
  async actionGetUserRegistrationsTable(
    context: MainContext,
    payload: { data: { user: number }; rowQuery: string }
  ) {
    try {
      const response = await api.getUserRegistrationsTable(
        context.rootState.main.token,
        payload.data,
        payload.rowQuery
      );
      if (response.data) {
        commitSetRecentRegistrations(context, response.data);
      }
    } catch (error) {
      commitSetRecentRegistrations(context, {
        next: 0,
        previous: 0,
        current: 0,
        count: 0,
        results: [],
      });
      await dispatchCheckApiError(context, error);
    }
  },
  async actionGetUserStructureList(context: MainContext) {
    try {
      const response = await api.getUserStructureList(
        context.rootState.main.token
      );
      return response.data;
    } catch (error) {
      await dispatchCheckApiError(context, error);
      return [];
    }
  },
  async actionGetUserStructureSearch(
    context: MainContext,
    payload: { data: { search: string }}) {
    try {
      const response = await api.getUserStructureSearch(
        context.rootState.main.token,
        payload.data
      );
      console.log("payload ", payload)
      return response.data;
    } catch (error) {
      await dispatchCheckApiError(context, error);
      return [];
    }
  },
  async actionSetUserBinaryStructureSide(
    context: MainContext,
    data: { side: string }
  ) {
    try {
      const response = await api.setUserBinaryStructureSide(
        context.rootState.main.token,
        data
      );
      return true;
    } catch (error) {
      await dispatchCheckApiError(context, error);
      return false;
    }
  },
  async actionGetAvailablePromoPackages(context: MainContext) {
    try {
      const response = await api.getAvailablePromoPackages(
        context.rootState.main.token
      );
      return response.data;
    } catch (error) {
      // await dispatchCheckApiError(context, error);
      return [];
    }
  },
  async actionGetSigmaRings(context: MainContext) {
    try {
      const response = await api.getSigmaRings(
        context.rootState.main.token
      );
      if (response.data) {
        commitSetSigmaRings(context, response.data);
      }
      return response.data;
    } catch (error) {
      await dispatchCheckApiError(context, error);
      return [];
    }
  },
  async actionGetUserSigmaRings(context: MainContext) {
    try {
      const response = await api.getUserSigmaRings(
        context.rootState.main.token
      );
      if (response.data) {
        commitSetUserSigmaRings(context, response.data);
      }
      return response.data;
    } catch (error) {
      // await dispatchCheckApiError(context, error);
      return [];
    }
  },
  async actionSubmitActivatePromocode(
    context: MainContext,
    payload: { promocode: string }
  ) {
    try {
      const response = await api.submitActivatePromocode(
        context.rootState.main.token,
        payload
      );
      return response.data.success;
    } catch (error) {
      await dispatchCheckApiError(context, error);
      dispatchCheckFormError(context, error);
      return false;
    }
  },
  async actionSubmitCreatePromocode(
    context: MainContext,
    payload: { promocode: number; standart: string; token: string }
  ) {
    try {
      const response = await api.submitCreatePromocode(
        context.rootState.main.token,
        payload
      );
      return response.data.promocode;
    } catch (error) {
      await dispatchCheckApiError(context, error);
      dispatchCheckFormError(context, error);
      return null;
    }
  },
  async actionGetFirstLineUsers(
    context: MainContext,
    payload: { data: any; rowQuery: string }
  ) {
    try {
      const response = await api.getFirstLineUsers(
        context.rootState.main.token,
        payload.data,
        payload.rowQuery
      );
      return response.data;
    } catch (error) {
      await dispatchCheckApiError(context, error);
      return [];
    }
  },
  async actionGetFirstLineUsersForUser(
    context: MainContext,
    payload: { data: {user: number; product?: number | null},rowQuery: string }
  ) {
    try {
      const response = await api.getFirstLineUsersForUser(
        context.rootState.main.token,
        payload.data,
        payload.rowQuery
      );
      return response.data;
    } catch (error) {
      await dispatchCheckApiError(context, error);
      return [];
    }
  },
  // 26.02.2026
  async actionGetUserPurchases(
    context: MainContext,
    payload: { user_id: number;}
  ) {
    try {
      const response = await api.getUserPurchases(
        context.rootState.main.token,
        payload
      );
      return response.data;
    } catch (error) {
      await dispatchCheckApiError(context, error);
      return [];
    }
  },
  async actionGetReferralStructureInfo(context: MainContext, payload: any) {
    try {
      const response = await api.getReferralStructureInfo(
        context.rootState.main.token,
        payload
      );
      commitSetUserRefInfo(context, response.data);
    } catch (error) {
      await dispatchCheckApiError(context, error);
      commitSetUserRefInfo(context, {
        users_count: 0,
        users_count_first_line: 0,
        levels_count: 0,
        //total_devices_in_all_branches: 0,
        max_branch_devices_count: 0,
        other_branches_devices_sum: 0,
        ring_purchases_count_in_ten_lines:0,
  t_package_purchases_count_in_ten_lines:0,
  ph_node_purchases_count_in_ten_lines:0,
  v_node_purchases_count_in_ten_lines:0,
  ring_purchases_count:0,
  t_package_purchases_count:0,
  ph_node_purchases_count:0,
  v_node_purchases_count:0,
      });
    }
  },
  async actionGetMonthlyTurnover(context: MainContext) {
    try {
      const response = await api.getMonthlyTurnover(
        context.rootState.main.token,
      );
      commitSetMonthlyTurnover(context, response.data);
    } catch (error) {
      await dispatchCheckApiError(context, error);
      commitSetMonthlyTurnover(context, {
        period: "",
        personal_turnover_month: "",
        mini_turnover_month: "",
        ten_line_turnover_month:"",
      });
    }
  },
  async actionGetDexSummerPromoStatus(context: MainContext, payload: any) {
    try {
      const response = await api.getDexSummerPromoStatus(
        context.rootState.main.token,
        payload
      );
      return response.data;
    } catch (error) {
      await dispatchCheckApiError(context, error);
      return null;
    }
  },
  // --- virtual nude upgrade //
  async actionGetVirtualNodeAvailableForUpgrade(context: MainContext, node: string) {
    try {
      const response = await api.getVirtualNodeAvailableForUpgrade(
        context.rootState.main.token,
        node
      );
      return response.data;
    } catch (error) {
      // await dispatchCheckApiError(context, error);
      return [];
    }
  },
  async actionSubmitUpgradeVirtualNode(
    context: MainContext,
    payload: { promocode: number; virtual_node: number; standart: string; token: string }
  ) {
    try {
      const response = await api.submitUpgradeVirtualNode(
        context.rootState.main.token,
        payload
      );
      return response;
    } catch (error) {
      await dispatchCheckApiError(context, error);
      dispatchCheckFormError(context, error);
      return null;
    }
  },
  // --- resident agreement //
  async actionSubmitResidentAgreement(context: MainContext, payload: any) {
    try {
      const response = await api.submitResidentAgreement(context.rootState.main.token, payload);
      return response.data;
    } catch (error) {
      await dispatchCheckApiError(context, error);
      return null;
    }
  },
  async actionGetResidentAgreement(context: MainContext) {
    try {
      const response = await api.getResidentAgreement(context.rootState.main.token);
      return response.data;
    } catch (error) {
      await dispatchCheckApiError(context, error);
      return null;
    }
  },
  async actionUpdateResidentAgreement(context: MainContext, payload: any) {
    try {
      const response = await api.updateResidentAgreement(context.rootState.main.token, payload);
      return response.data;
    } catch (error) {
      await dispatchCheckApiError(context, error);
      return null;
    }
  },
  // 13.04.2026
  async actionGetXcoinPackages(context: MainContext) {
    try {
      const response = await api.getXcoinPackagesList(context.rootState.main.token);
      if (response.data) {
        commitSetXcoinPackagesList(context, response.data);
      }
    } catch (error) {
      commitSetXcoinPackagesList(context, []);
      await dispatchCheckApiError(context, error);
    }
  },
  // 20.04.2026
  async actionGetUserXcoinPackages(context: MainContext) {
    try {
      const response = await api.getUserXcoinPackagesList(context.rootState.main.token);
      if (response.data) {
        commitSetUserXcoinPackagesList(context, response.data);
      }
    } catch (error) {
      // commitSetXcoinPackagesList(context, []);
      await dispatchCheckApiError(context, error);
    }
  },
  async actionGetXcoinActivePackages(context: MainContext) {
    try {
      const response = await api.getActiveXcoinPackages(context.rootState.main.token);
      if (response.data) {
        commitSetXcoinActivePackages(context, response.data);
      }
    } catch (error) {
      commitSetXcoinActivePackages(context, []);
      await dispatchCheckApiError(context, error);
    }
  },
  // 27.04.2026
  async actionGetPhysicalNodesList(context: MainContext) {
    try {
      const response = await api.getPhysicalNodesList(context.rootState.main.token);
      if (response.data) {
        commitSetPhysicalNodesList(context, response.data);
      }
    } catch (error) {
      commitSetPhysicalNodesList(context, []);
      await dispatchCheckApiError(context, error);
    }
  },
  async actionGetPhysicalNodesActive(context: MainContext) {
    try {
      const response = await api.getPhysicalNodesActive(context.rootState.main.token);
      if (response.data) {
        commitSetPhysicalNodesActive(context, response.data);
      }
    } catch (error) {
      commitSetPhysicalNodesActive(context, []);
      await dispatchCheckApiError(context, error);
    }
  },
  // Virtual Nodes List
  async actionGetVirtualNodesList(context: MainContext) {
    try {
      const response = await api.getVirtualNodesList(context.rootState.main.token);
      if (response.data) {
        commitSetVirtualNodesList(context, response.data);
      }
    } catch (error) {
      commitSetVirtualNodesList(context, []);
      await dispatchCheckApiError(context, error);
    }
  },
  async actionGetVirtualNodesActive(context: MainContext) {
    try {
      const response = await api.getVirtualNodesActive(context.rootState.main.token);
      if (response.data) {
        commitSetVirtualNodesActive(context, response.data);
      }
    } catch (error) {
      commitSetVirtualNodesActive(context, []);
      await dispatchCheckApiError(context, error);
    }
  },
  // confirm
   async actionPhysicalNodeConfirmInvoice(
    context: MainContext,
    data: { invoice: number, period: number, email: string | undefined }
  ) {
    try {
      const response = await api.physicalNodeConfirmInvoice(
        context.rootState.main.token,
        data
      );
      return { success: true, data: response.data };
    } catch (error) {
      // commitSetInvoiceResponse(context, null);
      await dispatchCheckApiError(context, error);
      return { 
      success: false, 
      status: error.response?.status,
      data: error.response?.data  // здесь будут email, detail, invoice
    };
    }
  },
  // physical node request
  async actionPhysicalNodePurchaseRequest(context: MainContext, payload: any) {
    try {
      const response = await api.postPhysicalNodePurchaseRequest(context.rootState.main.token, payload);
      return { success: true, data: response.data };
    } catch (error) {
      await dispatchCheckApiError(context, error);
      return null;
    }
  },
  async actionGetReferralConfirmation(context: MainContext) {
    try {
      const response = await api.getReferralConfirmation(context.rootState.main.token);
      return { success: true, data: response.data };
    } catch (error) {
      
      await dispatchCheckApiError(context, error);
      return { 
      success: false, 
      status: error.response?.status,
      data: error.response?.data  // здесь будут email, detail, invoice
    };
    }
  },
};

const { dispatch } = getStoreAccessors<MlmState | any, State>("");

export const dispAuthFetch = dispatch(actions.actionAuthFetch);
export const dispatchGetPackages = dispatch(actions.actionGetPackages);
export const dispatchGetActivePackage = dispatch(
  actions.actionGetActivePackage
);
export const dispatchMakeProfixoneLink = dispatch(
  actions.actionMakeProfixoneLink
);
export const dispatchGetCompanyStats = dispatch(actions.actionGetCompanyStats);
export const dispatchGetQualification = dispatch(
  actions.actionGetQualification
);
export const dispatchGetOrCreateInvoice = dispatch(
  actions.actionGetOrCreateInvoice
);
export const dispatchProcessConfirmInvoice = dispatch(
  actions.actionProcessConfirmInvoice
);
// export const dispatchGetOrCreateUpgradeInvoice = dispatch(actions.actionGetOrCreateUpgradeInvoice);
export const dispatchGetOrCreateOrderInvoice = dispatch(
  actions.actionGetOrCreateOrderInvoice
);
export const dispatchGetPaymentStatus = dispatch(
  actions.actionGetPaymentStatus
);
export const dispatchGetUserBinaryObject = dispatch(
  actions.actionGetUserBinaryObject
);
export const dispatchGetUserBinaryTree = dispatch(
  actions.actionGetUserBinaryTree
);
export const dispatchGetPurchaseStatisticsByPackages = dispatch(
  actions.actionGetPurchaseStatisticsByPackages
);
export const dispatchGetUserRegistrationsTable = dispatch(
  actions.actionGetUserRegistrationsTable
);
export const dispatchSetUserBinaryStructureSide = dispatch(
  actions.actionSetUserBinaryStructureSide
);
export const dispatchGetUserStructureList = dispatch(
  actions.actionGetUserStructureList
);
export const dispatchGetUserStructureSearch = dispatch(
  actions.actionGetUserStructureSearch
);
export const dispatchGetTransactionsList = dispatch(
  actions.actionGetTransactionsList
);
export const dispatchValidateDexnetAddress = dispatch(
  actions.actionValidateDexnetAddress
);
export const dispatchSubmitWithdrawRequest = dispatch(
  actions.actionSubmitWithdrawRequest
);
export const dispatchCheckDexfreedomTokenInfo = dispatch(
  actions.actionCheckDexfreedomTokenInfo
);
export const dispatchProcessPurchaseByUnit = dispatch(
  actions.actionProcessPurchaseByUnit
);
export const dispatchGetAvailablePromoPackages = dispatch(
  actions.actionGetAvailablePromoPackages
);
export const dispatchSubmitActivatePromocode = dispatch(
  actions.actionSubmitActivatePromocode
);
export const dispatchSubmitCreatePromocode = dispatch(
  actions.actionSubmitCreatePromocode
);
export const dispatchGetFirstLineUsers = dispatch(
  actions.actionGetFirstLineUsers
);
export const dispatchGetFirstLineUsersForUser = dispatch(
  actions.actionGetFirstLineUsersForUser
);
// 25.02.2026
export const dispatchGetUserPurchases = dispatch(
  actions.actionGetUserPurchases
);
export const dispatchGetReferralStructureInfo = dispatch(
  actions.actionGetReferralStructureInfo
);
export const dispatchGetDexSummerPromoStatus = dispatch(
  actions.actionGetDexSummerPromoStatus
);
export const dispatchRequestEmirexPayment = dispatch(
  actions.actionRequestEmirexPayment
);
export const dispatchGetEmirexEuroRate = dispatch(
  actions.actionGetEmirexEuroRate
);
export const dispatchGetVirtualNodeAvailableForUpgrade = dispatch(
  actions.actionGetVirtualNodeAvailableForUpgrade
);
export const dispatchSubmitUpgradeVirtualNode = dispatch(
  actions.actionSubmitUpgradeVirtualNode
);
export const dispatchGetSigmaRings = dispatch(
  actions.actionGetSigmaRings
);
export const dispatchGetUserSigmaRings = dispatch(
  actions.actionGetUserSigmaRings
);
export const dispatchSubmitResidentAgreement = dispatch(
  actions.actionSubmitResidentAgreement
);
export const dispatchGetResidentAgreement = dispatch(
  actions.actionGetResidentAgreement
);
export const dispatchUpdateResidentAgreement = dispatch(
  actions.actionUpdateResidentAgreement
);
// 13.04.2026
export const dispatchGetXcoinPackages = dispatch(
  actions.actionGetXcoinPackages
);
// 20.04.2026
export const dispatchGetUserXcoinPackages = dispatch(
  actions.actionGetUserXcoinPackages
);
export const dispatchGetXcoinActivePackages = dispatch(
  actions.actionGetXcoinActivePackages
);
// 27.04.2026
export const dispatchGetPhysicalNodesList = dispatch(
  actions.actionGetPhysicalNodesList
);
export const dispatchGetPhysicalNodesActive = dispatch(
  actions.actionGetPhysicalNodesActive
);
// virt nodes
export const dispatchGetVirtualNodesList = dispatch(
  actions.actionGetVirtualNodesList
);
export const dispatchGetVirtualNodesActive = dispatch(
  actions.actionGetVirtualNodesActive
);
export const dispatchPhysicalNodePurchaseRequest = dispatch(
  actions.actionPhysicalNodePurchaseRequest
);
export const dispatchPhysicalNodeConfirmInvoice = dispatch(
  actions.actionPhysicalNodeConfirmInvoice
);
export const dispatchGetReferralConfirmation = dispatch(
  actions.actionGetReferralConfirmation
);
// 31.05.2026
export const dispatchGetMonthlyTurnover = dispatch(
  actions.actionGetMonthlyTurnover
)